import { Building2, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FieldScope } from '@/lib/product-field-scope';

/**
 * Violet is the only hue left unclaimed in the app's semantic set — teal is
 * primary, red destructive, amber warning, green success, blue info, and
 * orange already means "warehouse" in this very form. Keeping location scope
 * on its own hue means the colour can carry exactly one meaning.
 *
 * Colour is never the only cue: every chip pairs it with an icon and a word,
 * so scope survives greyscale printing and colour blindness.
 */
export function ScopeChip({
  scope,
  locationName,
  className,
}: {
  scope: FieldScope;
  /** Named in the chip when scope is `location` — "Riverside Market only" reads
   *  far better than the abstract "Location-specific". */
  locationName?: string | null;
  className?: string;
}) {
  const base =
    'inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold leading-tight';

  if (scope === 'location') {
    return (
      <span
        className={cn(base, 'border-violet-200 bg-violet-50 text-violet-700', className)}
        title={
          locationName
            ? `Saves to ${locationName} only. Other locations keep their own value.`
            : 'Saves to the selected location only.'
        }
      >
        <MapPin className="h-3 w-3" aria-hidden="true" />
        {locationName ? `${locationName} only` : 'This location only'}
      </span>
    );
  }

  if (scope === 'override') {
    return (
      <span
        className={cn(base, 'border-amber-300 bg-amber-50 text-amber-700', className)}
        title="A product-wide default that each location can override."
      >
        Set per store
      </span>
    );
  }

  return (
    <span
      className={cn(base, 'border-border bg-muted text-muted-foreground', className)}
      title="One value shared by every location."
    >
      <Building2 className="h-3 w-3" aria-hidden="true" />
      All locations
    </span>
  );
}

/**
 * Field-level marker for a control whose scope differs from the card it sits
 * in. Decorative on its own — always pair it with the wording underneath the
 * field, so a dot is never asked to carry the meaning by itself.
 */
export function ScopeDot({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn('inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500 ring-2 ring-amber-100', className)}
    />
  );
}
